import Button from "app/storybookComponents/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faEye } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { GoalItem } from "../types";
import { getCreatedAtDate, getGoalProgress, getGoalStatus } from "../helpers";
import { Dropdown } from "react-bootstrap";
import {
  faBoxArchive,
  faLink,
  faThoughtBubble,
} from "@fortawesome/pro-regular-svg-icons";
import { useState } from "react";
import TableDropdownMenu from "app/components/Dropdowns/TableDropdownMenu";
import ReflectionProgressBar from "../Reflection/ReflectionProgressBar";
import ReflectionStatusBadge from "../Reflection/ReflectionStatusBadge";
import LinkedGoalsDrawer from "../LinkedGoalsDrawer";
import { Skeleton } from "app/shadcnComponents/ui/skeleton";

interface Props {
  goals: GoalItem[];
  onEdit: (goalId: GoalItem) => void;
  onReflect: (goalId: number) => void;
  onArchive: (goalId: number) => void;
  onDelete: (goalId: number) => void;
  onUnarchive: (goalId: number) => void;
  error?: string;
  isLoading?: boolean;
}

export default function OrganizationGoalsTable({
  goals,
  onEdit,
  error,
  isLoading = false,
  onReflect,
  onArchive,
  onDelete,
  onUnarchive,
}: Readonly<Props>) {
  const navigate = useNavigate();
  const [linkedGoalModalSelectedGoal, setLinkedGoalModalSelectedGoal] =
    useState<GoalItem | null>(null);

  return (
    <div className="goals-table-wrapper figtree-container">
      <table className="goals-table">
        <thead>
          <tr>
            <th style={{ width: "25%" }}>Goal</th>
            <th>Due</th>
            <th>Progress / Status</th>
            <th style={{ width: "18%" }}>Linked</th>
            <th />
          </tr>
        </thead>

        <tbody>
          {/* Inline loader while filtering */}
          {isLoading && (
            <tr className="loading-row">
              {Array.from({ length: 7 }).map((_, index) => (
                <td key={index}>
                  <div className="table-loader">
                    <Skeleton className="h-4 w-full" />
                  </div>
                </td>
              ))}
            </tr>
          )}

          {/* Error */}
          {!isLoading && error && goals.length === 0 && (
            <tr className="error-row">
              <td colSpan={7}>
                <div className="error-message">{error}</div>
              </td>
            </tr>
          )}

          {/* No data */}
          {!isLoading && !error && goals.length === 0 && (
            <tr className="no-data-row">
              <td colSpan={7}>No data found</td>
            </tr>
          )}

          {/* Data rows */}
          {!isLoading &&
            goals.map((goal) => {
              const status = getGoalStatus(goal);
              const progressPercent = getGoalProgress(goal);

              return (
                <tr key={goal.goalId}>
                  {/* GOAL */}
                  <td className="goal-title">
                    <p className="font-bold">{goal.title}</p>
                    <p className="text-muted">{goal.successCriteria}</p>
                  </td>

                  {/* DUE */}
                  <td>{getCreatedAtDate(goal.dueDateFormatted)}</td>

                  {/* PROGRESS */}
                  <td>
                    {status && progressPercent !== null ? (
                      <div className="progress-cell">
                        <ReflectionProgressBar
                          reflectionStatus={status}
                          progressPercent={progressPercent}
                        />

                        <ReflectionStatusBadge status={status} />
                      </div>
                    ) : (
                      <span className="no-progress">—</span>
                    )}
                  </td>

                  {/* VISIBILITY */}
                  <td>
                    <div>
                      <Button
                        variant="link"
                        onClick={() => setLinkedGoalModalSelectedGoal(goal)}
                      >
                        <FontAwesomeIcon icon={faLink} className="me-2" />
                        {goal.child_goals.length}
                      </Button>
                    </div>
                  </td>
                  {/* ACTION */}
                  <td className="actions-cell" style={{ position: "relative" }}>
                    <Dropdown
                      onSelect={(e) => {
                        switch (e) {
                          case "reflect":
                            return onReflect(goal.goalId);
                          case "viewProfile":
                            return navigate(
                              `/UserGuide/${goal.user?.userAccountId}`
                            );
                          case "unarchive":
                            return onUnarchive(goal.goalId);
                          case "archive":
                            return onArchive(goal.goalId);
                          case "edit":
                            return onEdit(goal);
                          case "delete":
                            return onDelete(goal.goalId);
                        }
                      }}
                      style={{ float: "right" }}
                    >
                      <Dropdown.Toggle
                        variant="outline-primary"
                        id="dropdown-basic"
                        className="no-caret ms-auto"
                      >
                        <FontAwesomeIcon icon="ellipsis" />
                      </Dropdown.Toggle>

                      <TableDropdownMenu>
                        {goal.archived ? (
                          <>
                            <Dropdown.Item eventKey="unarchive">
                              Unarchive
                            </Dropdown.Item>
                            <Dropdown.Divider />
                            <Dropdown.Item eventKey="delete" className="danger">
                              Delete Goal
                            </Dropdown.Item>
                          </>
                        ) : (
                          <>
                            <Dropdown.Item eventKey="edit">
                              <FontAwesomeIcon icon={faEdit} />
                              Edit Goal
                            </Dropdown.Item>
                            <Dropdown.Divider />
                            <Dropdown.Item eventKey="archive">
                              <FontAwesomeIcon icon={faBoxArchive} />
                              Archive
                            </Dropdown.Item>
                            <Dropdown.Divider />
                            <Dropdown.Item eventKey="reflect">
                              <FontAwesomeIcon icon={faThoughtBubble} />
                              Reflect
                            </Dropdown.Item>
                            <Dropdown.Divider />
                            <Dropdown.Item eventKey="viewProfile">
                              <FontAwesomeIcon icon={faEye} />
                              View Profile
                            </Dropdown.Item>
                          </>
                        )}
                      </TableDropdownMenu>
                    </Dropdown>
                  </td>
                </tr>
              );
            })}
        </tbody>
      </table>
      <LinkedGoalsDrawer
        isOpen={!!linkedGoalModalSelectedGoal}
        onClose={() => setLinkedGoalModalSelectedGoal(null)}
        parentGoal={linkedGoalModalSelectedGoal}
      />
    </div>
  );
}
